import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, Tooltip, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslate } from "react-admin";

import {
  CustomerServiceOrganizationBillingAccessDocument,
  CustomerServiceOrganizationBillingAccessQuery,
  CustomerServiceOrganizationBillingAccessQueryVariables,
  SetOrganizationBillingAccessDocument,
  SetOrganizationBillingAccessMutation,
  SetOrganizationBillingAccessMutationVariables,
} from "../generated/graphql";
import { client } from "../lib/apolloClient";
import { captureException } from "../lib/errorHandling";
import { BillingAccessDialogValue, OrganizationBillingAccountDialog } from "./OrganizationBillingAccountDialog";

type OrganizationBillingAccess =
  CustomerServiceOrganizationBillingAccessQuery["customerServiceOrganizationBillingAccess"][number];

type Props = {
  organizationId: string;
  organizationName: string;
  onAccountChanged: () => void;
  showAccountSummary?: boolean;
};

export const OrganizationBillingManagement = ({
  organizationId,
  organizationName,
  onAccountChanged,
  showAccountSummary = false,
}: Props) => {
  const translate = useTranslate();
  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);
  const pendingRef = useRef(false);
  const [access, setAccess] = useState<OrganizationBillingAccess | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [pending, setPending] = useState(false);
  const [reload, setReload] = useState(0);
  const [dialogAccess, setDialogAccess] = useState<BillingAccessDialogValue | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    const requestId = ++requestIdRef.current;
    const load = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const result = await client.query<
          CustomerServiceOrganizationBillingAccessQuery,
          CustomerServiceOrganizationBillingAccessQueryVariables
        >({
          query: CustomerServiceOrganizationBillingAccessDocument,
          variables: { organizationIds: [organizationId] },
          fetchPolicy: "network-only",
          context: { queryDeduplication: false },
        });
        const nextAccess = result.data.customerServiceOrganizationBillingAccess.find(
          status => status.organizationId === organizationId
        );
        if (!nextAccess) throw new Error("Missing organisation billing access");
        if (!active || !mountedRef.current || requestId !== requestIdRef.current) return;
        setAccess(nextAccess);
        setLoading(false);
      } catch (error) {
        captureException(error, "billing/access: load billing access");
        if (!active || !mountedRef.current || requestId !== requestIdRef.current) return;
        setLoadError(true);
        setLoading(false);
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [organizationId, reload]);

  const reloadAccess = useCallback(() => setReload(value => value + 1), []);

  const saveBillingAccess = async (accessEnabled: boolean) => {
    if (!access?.mappingVersion || pendingRef.current) return;
    const expectedMappingVersion = access.mappingVersion;
    pendingRef.current = true;
    setPending(true);
    setSaveError(false);
    try {
      const result = await client.mutate<
        SetOrganizationBillingAccessMutation,
        SetOrganizationBillingAccessMutationVariables
      >({
        mutation: SetOrganizationBillingAccessDocument,
        variables: { organizationId, accessEnabled, expectedMappingVersion },
      });
      const updated = result.data?.setOrganizationBillingAccess;
      if (
        !updated ||
        updated.organizationId !== organizationId ||
        updated.accessEnabled !== accessEnabled ||
        updated.mappingVersion !== expectedMappingVersion
      )
        throw new Error("Conflicting billing access response");
      if (mountedRef.current) setAccess(updated);
    } catch (error) {
      captureException(error, "billing/access: save billing access");
      if (mountedRef.current) setSaveError(true);
    } finally {
      pendingRef.current = false;
      if (mountedRef.current) setPending(false);
    }
  };

  const handleDialogReload = () => {
    if (!mountedRef.current) return;
    setDialogAccess(null);
    reloadAccess();
    onAccountChanged();
  };

  const handleDialogSaved = (updated: BillingAccessDialogValue) => {
    if (!mountedRef.current || updated.organizationId !== organizationId) return;
    setAccess(updated);
    setSaveError(false);
    setDialogAccess(null);
    onAccountChanged();
  };

  const actionsDisabled = loading || loadError || pending;
  const link = access && (
    <Button size="small" disabled={actionsDisabled} onClick={() => setDialogAccess(access)}>
      {translate("billingAccess.link")}
    </Button>
  );

  return (
    <Box
      component="section"
      aria-label={translate("billingAccess.label")}
      data-testid="organization-billing-management"
    >
      {loading && !access && (
        <Box role="status" aria-label={translate("billingAccess.loading")}>
          <CircularProgress size={20} />
        </Box>
      )}
      {loadError && (
        <Alert severity="error" action={<Button onClick={reloadAccess}>{translate("billingAccess.retry")}</Button>}>
          {translate("billingAccess.loadError")}
        </Alert>
      )}
      {access && (
        <>
          {showAccountSummary && access.salesforceAccountId && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              {translate("billingAccess.linkedAccount")}: {access.salesforceAccountName || access.salesforceAccountId}
            </Typography>
          )}
          {access.invoiceAccountConfigured ? (
            <Tooltip title={translate("billingAccess.accountScopeHint")}>
              <span>
                <FormControlLabel
                  label={translate("billingAccess.label")}
                  control={
                    <Checkbox
                      checked={access.accessEnabled}
                      disabled={actionsDisabled || !access.mappingConfigured || !access.mappingVersion}
                      inputProps={{
                        "aria-label": translate("billingAccess.checkboxLabel", { organization: organizationName }),
                      }}
                      onChange={event => void saveBillingAccess(event.target.checked)}
                    />
                  }
                />
              </span>
            </Tooltip>
          ) : (
            <Typography component="span" variant="caption" color="text.secondary">
              {translate("billingAccess.notAvailable")}
            </Typography>
          )}
          {saveError && (
            <>
              <Typography component="span" variant="caption" color="error">
                {translate("billingAccess.saveError")}
              </Typography>
              <Button size="small" disabled={pending} onClick={reloadAccess}>
                {translate("billingAccess.retry")}
              </Button>
            </>
          )}
          {link}
        </>
      )}
      {dialogAccess && (
        <OrganizationBillingAccountDialog
          access={dialogAccess}
          organizationName={organizationName}
          onClose={() => setDialogAccess(null)}
          onReload={handleDialogReload}
          onSaved={handleDialogSaved}
        />
      )}
    </Box>
  );
};
